import React, { useState, useCallback, useRef, useEffect, useMemo } from "react";
import { useTranslation } from 'react-i18next';
import { DndProvider, useDrag, useDrop } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { useFixedAppStyles } from './useAppStyles';
import {
  ArrowLeft,
  Volume2,
  RotateCw,
  CheckCircle,
  XCircle,
  Home
} from 'lucide-react';
import { motion } from 'framer-motion';
import type {
  LanguageCode,
  UserProgress
} from '../types/types';
import { getLanguageData } from '../data/languages';
import { useAudio } from '../hooks/hooks';
import useSentenceRevision, { type SentenceRevisionInfo } from '../hooks/useSentenceRevision';
import ConfirmationModal from './ConfirmationModal';
import { useSummaryAudio } from '../utils/summaryAudio';
import { useTheme } from './ThemeContext';
import SummaryScreen from './SummaryScreen';
import { getSentenceTranslation } from '../utils/localizedContent';
import { getRandomCongrats, getRandomProverb } from '../data/summaryData';
import './SentenceConstructionGame.css';
import './LearnMode.css';

import FloatingBackButton from './FloatingBackButton';

const ItemTypes = { WORD: "word" };

interface WordWithId {
  id: string;
  text: string;
  index: number;
}

interface FrenchWordWithId {
  id: string;
  text: string;
  index: number;
  isCorrect?: boolean;
}

interface TranslationState {
  wolofSentence: string;
  correctFrenchWords: string[];
  availableFrenchWords: FrenchWordWithId[];
  constructedTranslation: FrenchWordWithId[];
  distractorWords: string[];
}

interface SentenceRevisionGameProps {
  languageCode: LanguageCode;
  onBackToCategories: () => void;
  onAnswer?: (isCorrect: boolean) => void;
  userProgress?: UserProgress;
  disableConfirmationModal?: boolean;
  filterCategories?: string[];
  saveProgressOnSummary?: (
    progress: UserProgress,
    context?: {
      mode?: string;
      description?: string;
      includeGoogleDrive?: boolean;
      sessionStats?: any;
      sentenceRevisionData?: any;
    }
  ) => Promise<void>;
}

interface DraggableWordProps {
  word: WordWithId;
  index: number;
  moveWord: (dragIndex: number, hoverIndex: number) => void;
  isCorrect: boolean;
  onWordClick?: (word: WordWithId) => void;
}

const DraggableWord: React.FC<DraggableWordProps> = ({ word, index, moveWord, isCorrect, onWordClick }) => {
  const ref = useRef<HTMLDivElement>(null);

  const [{ isDragging }, drag] = useDrag({
    type: ItemTypes.WORD,
    item: { type: ItemTypes.WORD, id: word.id, index },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  const [, drop] = useDrop({
    accept: ItemTypes.WORD,
    hover(item: { index: number }, monitor) {
      if (!ref.current) return;
      const dragIndex = item.index;
      const hoverIndex = index;
      if (dragIndex === hoverIndex) return;

      moveWord(dragIndex, hoverIndex);
      item.index = hoverIndex;
    }
  });

  drag(drop(ref));

  return (
    <div
      ref={ref}
      className={`modern-word-item ${isDragging ? 'dragging' : ''} ${isCorrect ? 'success' : ''} constructed-word`}
      style={{ cursor: 'pointer' }}
      onClick={() => onWordClick?.(word)}
    >
      {word.text}
    </div>
  );
};

const DraggableFrenchWord: React.FC<{
  word: FrenchWordWithId;
  index: number;
  moveWord: (dragIndex: number, hoverIndex: number) => void;
  isCorrect: boolean;
  source: 'available' | 'constructed';
  onWordClick?: (word: FrenchWordWithId) => void;
}> = ({ word, index, moveWord, isCorrect, source, onWordClick }) => {
  const ref = useRef<HTMLDivElement>(null);

  const [{ isDragging }, drag] = useDrag({
    type: ItemTypes.WORD,
    item: { type: ItemTypes.WORD, id: word.id, index, source, word },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  const [, drop] = useDrop({
    accept: ItemTypes.WORD,
    hover(item: { index: number; source: string }, monitor) {
      if (!ref.current || source !== 'constructed' || item.source !== 'constructed') return;
      
      const dragIndex = item.index;
      const hoverIndex = index;
      if (dragIndex === hoverIndex) return;

      moveWord(dragIndex, hoverIndex);
      item.index = hoverIndex;
    }
  });

  if (source === 'constructed') {
    drag(drop(ref));
  } else {
    drag(ref);
  }

  return (
    <div
      ref={ref}
      className={`modern-word-item ${isDragging ? 'dragging' : ''} ${isCorrect ? 'success' : ''} ${source === 'available' ? 'available-french-word' : 'constructed-french-word'}`}
      onClick={() => onWordClick?.(word)}
      style={{ cursor: 'pointer' }}
    >
      {word.text}
    </div>
  );
};

const TranslationDropZone: React.FC<{
  constructedWords: FrenchWordWithId[];
  onWordDrop: (word: FrenchWordWithId) => void;
  moveWord: (dragIndex: number, hoverIndex: number) => void;
  isCorrect: boolean;
  onWordClick?: (word: FrenchWordWithId) => void;
}> = ({ constructedWords, onWordDrop, moveWord, isCorrect, onWordClick }) => {
  const { t } = useTranslation();
  const [{ isOver }, drop] = useDrop({
    accept: ItemTypes.WORD,
    drop: (item: { id: string; source: string; word?: FrenchWordWithId }) => {
      if (item.source === 'available' && item.word) {
        onWordDrop(item.word);
      }
    },
    collect: (monitor) => ({
      isOver: monitor.isOver(),
    }),
  });

  return (
    <div
      ref={drop}
      className={`modern-construction-area translation-drop-zone ${isOver ? 'drop-over' : ''} ${isCorrect ? 'success' : ''}`}
    >
      {constructedWords.length === 0 ? (
        <div className="drop-zone-placeholder">
          {t('sentenceRevision.dropPlaceholder')}
        </div>
      ) : (
        constructedWords.map((word, index) => (
          <DraggableFrenchWord
            key={word.id}
            word={word}
            index={index}
            moveWord={moveWord}
            isCorrect={isCorrect}
            source="constructed"
            onWordClick={onWordClick}
          />
        ))
      )}
    </div>
  );
};

const SentenceRevisionGame: React.FC<SentenceRevisionGameProps> = ({
  languageCode,
  onBackToCategories,
  onAnswer,
  userProgress,
  disableConfirmationModal = false,
  saveProgressOnSummary,
  filterCategories
}) => {
  const playAudio = useAudio();
  const { playSummaryAudio } = useSummaryAudio();
  const { theme } = useTheme();
  const { t, i18n } = useTranslation();
  
  const {
    revisionProgress,
    updateRevisionProgress,
    getSessionHistory,
    finishSession,
    resetSession,
    sessionState,
    isLearnedSentence
  } = useSentenceRevision(languageCode);
  
  // ✅ SIMPLIFIÉ: Plus de mode 'selection'
  const [mode, setMode] = useState<'test' | 'summary'>('test');
  const [currentSentenceIndex, setCurrentSentenceIndex] = useState(0);
  const [constructedWords, setConstructedWords] = useState<WordWithId[]>([]);
  const [availableWords, setAvailableWords] = useState<WordWithId[]>([]);
  const [attempts, setAttempts] = useState(0);
  const [showCorrection, setShowCorrection] = useState(false);
  const [isCorrect, setIsCorrect] = useState(false);
  const [score, setScore] = useState(0);
  const [totalPhrases, setTotalPhrases] = useState(0);

  const [translationState, setTranslationState] = useState<TranslationState | null>(null);
  const [isTranslationCorrect, setIsTranslationCorrect] = useState(false);
  const [showTranslationCorrection, setShowTranslationCorrection] = useState(false);

  const [revisionSentences, setRevisionSentences] = useState<SentenceRevisionInfo[]>([]);
  const [revisionMode, setRevisionMode] = useState<'drag-drop' | 'text-input' | 'translation'>('drag-drop');
  const [revisionResults, setRevisionResults] = useState<Array<{
    sentence: SentenceRevisionInfo;
    isCorrect: boolean;
    timestamp: number;
  }>>([]);

  const [textInput, setTextInput] = useState('');
  const [showTextCorrection, setShowTextCorrection] = useState(false);

  const [showExitModal, setShowExitModal] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState<(() => void) | null>(null);

  // ✅ États pour la sauvegarde (sans XP)
  const [saveCompleted, setSaveCompleted] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false); // ✅ NOUVEAU: Flag d'initialisation

  const summaryCongrats = useMemo(() => getRandomCongrats(languageCode), [mode]);
  const summaryProverb  = useMemo(() => getRandomProverb(languageCode),  [mode]);

  useFixedAppStyles('review');

  const getAllLearnedSentences = useCallback((): SentenceRevisionInfo[] => {
    const history = getSessionHistory();
    const learned = history.filter(s => s.isCorrect && s.original && s.french);
    return learned;
  }, [getSessionHistory]);

  const getLearnedSentencesCount = useCallback(() => {
    return getAllLearnedSentences().length;
  }, [getAllLearnedSentences]);

  const createWordsWithIds = useCallback((words: string[]): WordWithId[] => {
    return words.map((word, index) => ({
      id: `${word}-${index}-${Math.random()}`,
      text: word,
      index
    }));
  }, []);

  const createFrenchWordsWithIds = useCallback((words: string[]): FrenchWordWithId[] => {
    return words.map((word, index) => ({
      id: `french-${word}-${index}-${Math.random()}`,
      text: word,
      index
    }));
  }, []);

  // ✅ Fonction pour normaliser le texte (enlever accents et ponctuation)
  const normalizeText = useCallback((text: string): string => {
    return text
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '') // Enlever les accents
      .replace(/[.,!?;:«»"'"]/g, '') // Enlever la ponctuation
      .replace(/\s+/g, ' ') // Normaliser les espaces
      .trim();
  }, []);

  const cleanSentence = useCallback((sentence: string): string => {
    return sentence
      .replace(/[.,!?;:«»"'"]/g, '')
      .replace(/\s+/g, ' ')
      .trim();
  }, []);

  const cleanWord = useCallback((word: string): string => {
    return word.replace(/[.,!?;:«»"'"]/g, '').trim();
  }, []);

  const generateDistractorWords = useCallback((currentFrench: string): string[] => {
    // Filler words used as distractor tiles — picked by interface language so
    // they never mix with a localized target sentence (e.g. English content
    // words alongside French "le"/"la" fillers would be an obvious tell).
    const commonWords = i18n.language === 'en'
      ? ['the', 'a', 'an', 'and', 'or', 'but', 'with', 'for', 'in', 'on']
      : ['le', 'la', 'les', 'un', 'une', 'des', 'et', 'ou', 'mais', 'avec', 'pour', 'dans', 'sur'];
    const currentWords = cleanSentence(currentFrench).toLowerCase().split(' ');
    const distractors = commonWords.filter(word => !currentWords.includes(word));
    return distractors.slice(0, 3);
  }, [cleanSentence, i18n.language]);

  const shuffleArray = <T extends any>(array: T[]): T[] => {
    const newArray = [...array];
    for (let i = newArray.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [newArray[i], newArray[j]] = [newArray[j], newArray[i]];
    }
    return newArray;
  };

  const handleNavigationWithConfirmation = useCallback((navigationCallback: () => void) => {
    const isInActiveRevision = mode === 'test' && currentSentenceIndex < revisionSentences.length;
    const hasRevisionProgress = revisionResults.length > 0 || score > 0;
    
    if (isInActiveRevision || hasRevisionProgress) {
      setPendingNavigation(() => navigationCallback);
      setShowExitModal(true);
    } else {
      navigationCallback();
    }
  }, [mode, currentSentenceIndex, revisionSentences.length, revisionResults.length, score]);

  const handleExitConfirm = useCallback(() => {
    setShowExitModal(false);
    if (pendingNavigation) {
      pendingNavigation();
      setPendingNavigation(null);
    }
  }, [pendingNavigation]);

  const handleExitCancel = useCallback(() => {
    setShowExitModal(false);
    setPendingNavigation(null);
  }, []);

  const prepareRevisionSentence = useCallback((sentence: SentenceRevisionInfo) => {
    const wordsToUse = sentence.words || sentence.original.split(' ');
    setConstructedWords([]);
    setAvailableWords(createWordsWithIds(shuffleArray(wordsToUse)));
    setAttempts(0);
    setShowCorrection(false);
    setIsCorrect(false);
  }, [createWordsWithIds]);

  // Localisé ici : la cible de construction, les tuiles et les distracteurs
  // (generateDistractorWords) dérivent tous de cette même valeur localisée,
  // donc ordre correct et pool de tuiles restent mutuellement cohérents.
  const prepareTranslationMode = useCallback((sentence: SentenceRevisionInfo) => {
    const localizedSentence = getSentenceTranslation(sentence, i18n.language);
    const cleanedFrench = cleanSentence(localizedSentence);
    const frenchWords = cleanedFrench.split(' ');
    const distractorWords = generateDistractorWords(localizedSentence);

    const allAvailableWords = [...frenchWords, ...distractorWords];
    const shuffledWords = shuffleArray(allAvailableWords);

    const translationState: TranslationState = {
      wolofSentence: sentence.original,
      correctFrenchWords: frenchWords,
      availableFrenchWords: createFrenchWordsWithIds(shuffledWords),
      constructedTranslation: [],
      distractorWords
    };

    setTranslationState(translationState);
    setIsTranslationCorrect(false);
    setShowTranslationCorrection(false);
  }, [cleanSentence, generateDistractorWords, createFrenchWordsWithIds, i18n.language]);

  // ✅ LANCEMENT AUTO AU MONTAGE - UNE SEULE FOIS
  useEffect(() => {
    if (isInitialized) {
     
      return;
    }
    
  
    
    // Fonction pour charger directement depuis localStorage
    const loadSentencesDirectly = (): SentenceRevisionInfo[] => {
      const sentencesMap = new Map<string, SentenceRevisionInfo>(); // ✅ Utiliser une Map pour éviter les doublons
      
      // 1. Charger depuis sentence-revision-{lang}-sessionHistory
      const revisionKey = `sentence-revision-${languageCode}-sessionHistory`;
      const revisionData = localStorage.getItem(revisionKey);
      
      if (revisionData) {
        try {
          const parsed = JSON.parse(revisionData);
          if (Array.isArray(parsed)) {
            parsed.forEach((s: any) => {
              if (s.original && s.french && s.isCorrect) {
                // ✅ Utiliser l'original comme clé unique
                const key = `${s.original.toLowerCase().trim()}-${s.category || 'default'}`;
                if (!sentencesMap.has(key)) {
                  sentencesMap.set(key, s);
                }
              }
            });
          
          }
        } catch (error) {
}
      }
      
      // 2. Charger aussi depuis sentence-progress-{lang}
      const progressKey = `sentence-progress-${languageCode}`;
      const progressData = localStorage.getItem(progressKey);
      
      if (progressData) {
        try {
          const progressSessions = JSON.parse(progressData);
          if (Array.isArray(progressSessions)) {
            progressSessions.forEach((session: any) => {
              if (session.masteredSentences && Array.isArray(session.masteredSentences)) {
                session.masteredSentences.forEach((sentence: any) => {
                  if (sentence.original && sentence.french && sentence.mastered) {
                    // ✅ Vérifier si pas déjà présente avec la même clé
                    const key = `${sentence.original.toLowerCase().trim()}-${sentence.category || session.category || 'default'}`;
                    if (!sentencesMap.has(key)) {
                      sentencesMap.set(key, {
                        original: sentence.original,
                        french: sentence.french,
                        audio: sentence.audio,
                        category: sentence.category || session.category || 'Salutations',
                        timestamp: sentence.timestamp || Date.now(),
                        nextReview: (sentence.timestamp || Date.now()) + (24 * 60 * 60 * 1000),
                        type: 'sentence',
                        words: sentence.words || sentence.original.split(' '),
                        isCorrect: true
                      });
                    }
                  }
                });
              }
            });
          
          }
        } catch (error) {
}
      }
      
      // ✅ Convertir la Map en Array, filtrer par unité si filterCategories fourni
      const all = Array.from(sentencesMap.values());
      if (!filterCategories || filterCategories.length === 0) return all;
      return all.filter(s => filterCategories.includes(s.category || ''));
    };
    
    // Attendre que le hook soit initialisé
    const checkAndStart = () => {
      const allSentences = loadSentencesDirectly();
      
   
      
      if (allSentences.length === 0) {
      
        alert('Aucune phrase apprise pour le moment ! Commencez par apprendre des phrases.');
        onBackToCategories();
        return;
      }
      
     
      
      const shuffled = [...allSentences].sort(() => Math.random() - 0.5);
      const sentencesToRevise = shuffled.slice(0, Math.min(5, shuffled.length)); // ✅ Maximum 5 phrases
      
     
      
      setRevisionSentences(sentencesToRevise);
      setCurrentSentenceIndex(0);
      setScore(0);
      setTotalPhrases(sentencesToRevise.length);
      setRevisionResults([]);
      
      // 'text-input' requires typing the Kama transliteration from memory,
      // including Egyptological Latin Extended-D letters (ḥ ṯ ḫ ḏ š ꜣ ꜥ)
      // that don't exist on any standard keyboard — excluded for Kama,
      // leaving the two tile/tap-based modes (drag-drop, translation),
      // which never require typing the source language.
      const modes = languageCode === 'ka' ? ['drag-drop', 'translation'] : ['drag-drop', 'text-input', 'translation'];
      const randomMode = modes[Math.floor(Math.random() * modes.length)] as 'drag-drop' | 'text-input' | 'translation';
      setRevisionMode(randomMode);
      
      if (randomMode === 'drag-drop') {
        prepareRevisionSentence(sentencesToRevise[0]);
      } else if (randomMode === 'translation') {
        prepareTranslationMode(sentencesToRevise[0]);
      } else {
        setTextInput('');
        setShowTextCorrection(false);
        setIsCorrect(false);
      }
      
      resetSession();
      setIsInitialized(true); // ✅ Marquer comme initialisé
     
    };
    
    // Attendre un peu pour laisser le hook s'initialiser
    const timer = setTimeout(checkAndStart, 500);

    return () => clearTimeout(timer);
  // filterCategories est intentionnellement inclus pour capturer la valeur correcte au montage
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterCategories]);

  const handleRevisionAnswer = useCallback(async (answer: string) => {
    if (!revisionSentences[currentSentenceIndex]) return;

    const currentSentence = revisionSentences[currentSentenceIndex];
    const constructedText = normalizeText(answer);
    const correctText = normalizeText(currentSentence.original);
    const isAnswerCorrect = constructedText === correctText;
    
    setIsCorrect(isAnswerCorrect);
    onAnswer?.(isAnswerCorrect);

    // ✅ Vérifier si cette phrase a déjà été testée
    const alreadyTested = revisionResults.some(r => r.sentence.original === currentSentence.original);
    
    const revisionResult = {
      sentence: currentSentence,
      isCorrect: isAnswerCorrect,
      timestamp: Date.now()
    };
    
    // ✅ Ajouter seulement si pas déjà testée
    if (!alreadyTested) {
      setRevisionResults(prev => [...prev, revisionResult]);
    } else {
      // ✅ Mettre à jour le résultat existant
      setRevisionResults(prev => 
        prev.map(r => 
          r.sentence.original === currentSentence.original 
            ? { ...r, isCorrect: r.isCorrect && isAnswerCorrect } // Réussi seulement si les deux modes sont réussis
            : r
        )
      );
    }

    if (isAnswerCorrect) {
      // ✅ Ne plus incrémenter score, on utilise revisionResults
      setTimeout(() => {
        if (currentSentenceIndex < revisionSentences.length - 1) {
          const nextIndex = currentSentenceIndex + 1;
          setCurrentSentenceIndex(nextIndex);
          
          // 'text-input' requires typing the Kama transliteration from memory,
      // including Egyptological Latin Extended-D letters (ḥ ṯ ḫ ḏ š ꜣ ꜥ)
      // that don't exist on any standard keyboard — excluded for Kama,
      // leaving the two tile/tap-based modes (drag-drop, translation),
      // which never require typing the source language.
      const modes = languageCode === 'ka' ? ['drag-drop', 'translation'] : ['drag-drop', 'text-input', 'translation'];
          const randomMode = modes[Math.floor(Math.random() * modes.length)] as 'drag-drop' | 'text-input' | 'translation';
          setRevisionMode(randomMode);
          
          if (randomMode === 'drag-drop') {
            prepareRevisionSentence(revisionSentences[nextIndex]);
          } else if (randomMode === 'translation') {
            prepareTranslationMode(revisionSentences[nextIndex]);
          } else {
            setTextInput('');
            setShowTextCorrection(false);
            setIsCorrect(false);
          }
        } else {
          setMode('summary');
        }
      }, 1000);
    } else {
      setAttempts(prev => prev + 1);
      if (attempts + 1 >= 2) {
        setShowCorrection(true);
      }
    }
  }, [
    revisionSentences,
    currentSentenceIndex,
    onAnswer,
    attempts,
    prepareRevisionSentence,
    prepareTranslationMode,
    revisionResults,
    normalizeText
  ]);

  const handleTextInputAnswer = useCallback(async () => {
    if (!revisionSentences[currentSentenceIndex] || !textInput.trim()) return;

    const currentSentence = revisionSentences[currentSentenceIndex];
    const userAnswer = normalizeText(textInput);
    const correctText = normalizeText(currentSentence.original);
    const isAnswerCorrect = userAnswer === correctText;
    
    setIsCorrect(isAnswerCorrect);
    onAnswer?.(isAnswerCorrect);

    // ✅ Vérifier si cette phrase a déjà été testée
    const alreadyTested = revisionResults.some(r => r.sentence.original === currentSentence.original);
    
    const revisionResult = {
      sentence: revisionSentences[currentSentenceIndex],
      isCorrect: isAnswerCorrect,
      timestamp: Date.now()
    };
    
    // ✅ Ajouter seulement si pas déjà testée
    if (!alreadyTested) {
      setRevisionResults(prev => [...prev, revisionResult]);
    } else {
      // ✅ Mettre à jour le résultat existant
      setRevisionResults(prev => 
        prev.map(r => 
          r.sentence.original === currentSentence.original 
            ? { ...r, isCorrect: r.isCorrect && isAnswerCorrect }
            : r
        )
      );
    }

    if (isAnswerCorrect) {
      // ✅ Ne plus incrémenter score, on utilise revisionResults
      setTimeout(() => {
        if (currentSentenceIndex < revisionSentences.length - 1) {
          const nextIndex = currentSentenceIndex + 1;
          setCurrentSentenceIndex(nextIndex);
          
          // 'text-input' requires typing the Kama transliteration from memory,
      // including Egyptological Latin Extended-D letters (ḥ ṯ ḫ ḏ š ꜣ ꜥ)
      // that don't exist on any standard keyboard — excluded for Kama,
      // leaving the two tile/tap-based modes (drag-drop, translation),
      // which never require typing the source language.
      const modes = languageCode === 'ka' ? ['drag-drop', 'translation'] : ['drag-drop', 'text-input', 'translation'];
          const randomMode = modes[Math.floor(Math.random() * modes.length)] as 'drag-drop' | 'text-input' | 'translation';
          setRevisionMode(randomMode);
          
          if (randomMode === 'drag-drop') {
            prepareRevisionSentence(revisionSentences[nextIndex]);
          } else if (randomMode === 'translation') {
            prepareTranslationMode(revisionSentences[nextIndex]);
          } else {
            setTextInput('');
            setShowTextCorrection(false);
            setIsCorrect(false);
          }
        } else {
          setMode('summary');
        }
      }, 1000);
    } else {
      setAttempts(prev => prev + 1);
      if (attempts + 1 >= 2) {
        setShowTextCorrection(true);
      }
    }
  }, [
    revisionSentences,
    currentSentenceIndex,
    textInput,
    onAnswer,
    attempts,
    prepareRevisionSentence,
    prepareTranslationMode,
    revisionResults,
    normalizeText
  ]);

  const moveWord = useCallback((dragIndex: number, hoverIndex: number) => {
    const dragWord = constructedWords[dragIndex];
    const newWords = [...constructedWords];
    newWords.splice(dragIndex, 1);
    newWords.splice(hoverIndex, 0, dragWord);
    setConstructedWords(newWords.map((word, index) => ({ ...word, index })));
  }, [constructedWords]);

  const handleWordDrop = useCallback((word: WordWithId) => {
    setConstructedWords(prev => [...prev, { ...word, index: prev.length }]);
    setAvailableWords(prev => prev.filter(w => w.id !== word.id));
  }, []);

  const removeWordByClick = useCallback((wordToRemove: WordWithId) => {
    if (isCorrect) return;
    
    const wordIndex = constructedWords.findIndex(w => w.id === wordToRemove.id);
    if (wordIndex === -1) return;
    
    const newConstructedWords = constructedWords.filter(w => w.id !== wordToRemove.id);
    setConstructedWords(newConstructedWords);
    
    setAvailableWords(prev => [...prev, { ...wordToRemove, index: prev.length }]);
  }, [constructedWords, isCorrect]);

  const handleFrenchWordDrop = useCallback((word: FrenchWordWithId) => {
    if (!translationState || isTranslationCorrect) return;
    
    setTranslationState(prev => {
      if (!prev) return null;
      
      return {
        ...prev,
        constructedTranslation: [...prev.constructedTranslation, { ...word, index: prev.constructedTranslation.length }],
        availableFrenchWords: prev.availableFrenchWords.filter(w => w.id !== word.id)
      };
    });
  }, [translationState, isTranslationCorrect]);

  const removeFrenchWordByClick = useCallback((wordToRemove: FrenchWordWithId) => {
    if (!translationState || isTranslationCorrect) return;
    
    const wordIndex = translationState.constructedTranslation.findIndex(w => w.id === wordToRemove.id);
    if (wordIndex === -1) return;
    
    setTranslationState(prev => {
      if (!prev) return null;
      
      return {
        ...prev,
        constructedTranslation: prev.constructedTranslation.filter(w => w.id !== wordToRemove.id),
        availableFrenchWords: [...prev.availableFrenchWords, { ...wordToRemove, index: prev.availableFrenchWords.length }]
      };
    });
  }, [translationState, isTranslationCorrect]);

  const moveTranslationWord = useCallback((dragIndex: number, hoverIndex: number) => {
    if (!translationState) return;
    
    const dragWord = translationState.constructedTranslation[dragIndex];
    const newWords = [...translationState.constructedTranslation];
    newWords.splice(dragIndex, 1);
    newWords.splice(hoverIndex, 0, dragWord);
    
    setTranslationState(prev => ({
      ...prev!,
      constructedTranslation: newWords.map((word, index) => ({ ...word, index }))
    }));
  }, [translationState]);

  const handleTranslationAnswer = useCallback(() => {
    if (!translationState) return;
    
    const constructedText = translationState.constructedTranslation.map(w => cleanWord(w.text)).join(' ');
    const correctText = translationState.correctFrenchWords.map(w => cleanWord(w)).join(' ');
    const isAnswerCorrect = constructedText.toLowerCase() === correctText.toLowerCase();
    
    setIsTranslationCorrect(isAnswerCorrect);
    onAnswer?.(isAnswerCorrect);

    // ✅ Vérifier si cette phrase a déjà été testée
    const currentSentence = revisionSentences[currentSentenceIndex];
    const alreadyTested = revisionResults.some(r => r.sentence.original === currentSentence.original);
    
    const revisionResult = {
      sentence: revisionSentences[currentSentenceIndex],
      isCorrect: isAnswerCorrect,
      timestamp: Date.now()
    };
    
    // ✅ Ajouter seulement si pas déjà testée
    if (!alreadyTested) {
      setRevisionResults(prev => [...prev, revisionResult]);
    } else {
      // ✅ Mettre à jour le résultat existant
      setRevisionResults(prev => 
        prev.map(r => 
          r.sentence.original === currentSentence.original 
            ? { ...r, isCorrect: r.isCorrect && isAnswerCorrect }
            : r
        )
      );
    }

    if (isAnswerCorrect) {
      // ✅ Ne plus incrémenter score, on utilise revisionResults
      setTimeout(() => {
        if (currentSentenceIndex < revisionSentences.length - 1) {
          const nextIndex = currentSentenceIndex + 1;
          setCurrentSentenceIndex(nextIndex);
          
          // 'text-input' requires typing the Kama transliteration from memory,
      // including Egyptological Latin Extended-D letters (ḥ ṯ ḫ ḏ š ꜣ ꜥ)
      // that don't exist on any standard keyboard — excluded for Kama,
      // leaving the two tile/tap-based modes (drag-drop, translation),
      // which never require typing the source language.
      const modes = languageCode === 'ka' ? ['drag-drop', 'translation'] : ['drag-drop', 'text-input', 'translation'];
          const randomMode = modes[Math.floor(Math.random() * modes.length)] as 'drag-drop' | 'text-input' | 'translation';
          setRevisionMode(randomMode);
          
          if (randomMode === 'drag-drop') {
            prepareRevisionSentence(revisionSentences[nextIndex]);
          } else if (randomMode === 'translation') {
            prepareTranslationMode(revisionSentences[nextIndex]);
          } else {
            setTextInput('');
            setShowTextCorrection(false);
            setIsCorrect(false);
          }
        } else {
          setMode('summary');
        }
      }, 1000);
    } else {
      setAttempts(prev => prev + 1);
      if (attempts + 1 >= 2) {
        setShowTranslationCorrection(true);
      }
    }
  }, [
    translationState,
    cleanWord,
    onAnswer,
    revisionSentences,
    currentSentenceIndex,
    attempts,
    prepareRevisionSentence,
    prepareTranslationMode,
    revisionResults
  ]);

  const handlePlayAudio = useCallback(async (audioPath?: string) => {
    if (!audioPath) return;
    try {
      await playAudio(audioPath);
    } catch (error) {
}
  }, [playAudio]);

  const handleReset = useCallback(() => {
    if (revisionMode === 'drag-drop') {
      if (revisionSentences[currentSentenceIndex]) {
        prepareRevisionSentence(revisionSentences[currentSentenceIndex]);
      }
    } else if (revisionMode === 'translation') {
      if (revisionSentences[currentSentenceIndex]) {
        prepareTranslationMode(revisionSentences[currentSentenceIndex]);
      }
    } else if (revisionMode === 'text-input') {
      setTextInput('');
      setShowTextCorrection(false);
      setIsCorrect(false);
    }
    setAttempts(0);
    setShowCorrection(false);
  }, [revisionMode, revisionSentences, currentSentenceIndex, prepareRevisionSentence, prepareTranslationMode]);

  // ✅ FONCTION DE SAUVEGARDE (sans XP)
  const handleRevisionSummaryComplete = useCallback(async () => {
    if (!saveProgressOnSummary) {
return;
    }

   
    setIsSaving(true);

    try {
      const masteredResults = revisionResults.filter(r => r.isCorrect);

      

      // ✅ Jouer l'audio de résumé
      playSummaryAudio();

      const revisionSession = {
        id: `revision-${Date.now()}`,
        startTime: Date.now(),
        endTime: Date.now(),
        language: languageCode,
        sentences: masteredResults.map(r => ({
          original: r.sentence.original,
          french: r.sentence.french,
          audio: r.sentence.audio,
          category: r.sentence.category,
          mastered: r.isCorrect,
          timestamp: Date.now(),
          words: r.sentence.words,
          interval: 1,
          attempts: 1,
          firstAttemptCorrect: r.isCorrect
        })),
        stats: {
          score: masteredResults.length,
          totalItems: revisionResults.length,
          accuracy: revisionResults.length > 0 
            ? (masteredResults.length / revisionResults.length) * 100 
            : 0,
          xpGained: 0
        }
      };

      const storageKey = `sentence-revision-sessions-${languageCode}`;
      const existingSessions = JSON.parse(localStorage.getItem(storageKey) || '[]');
      const updatedSessions = [...existingSessions, revisionSession];
      localStorage.setItem(storageKey, JSON.stringify(updatedSessions));

      const revisionProgress: UserProgress = {
        learnedWords: new Set(masteredResults.map(r => r.sentence.original)),
        wordsToReview: new Set(),
        wordProgress: {},
        recentlyLearnedWords: masteredResults.map(r => ({
          word: r.sentence.original,
          category: r.sentence.category,
          language: languageCode,
          timestamp: Date.now(),
          translation: r.sentence.french,
          audio: r.sentence.audio
        })),
        language: languageCode
      };

      for (const result of revisionResults) {
        await updateRevisionProgress(result.sentence, result.isCorrect);
      }

      await saveProgressOnSummary(revisionProgress, {
        mode: 'sentenceRevision',
        description: 'Sauvegarde session Révision de Phrases',
        includeGoogleDrive: true,
        sessionStats: {
          phrasesReviewed: revisionResults.length
        },
        sentenceRevisionData: revisionSession
      });

      window.dispatchEvent(new CustomEvent('sentenceRevisionCompleted', {
        detail: {
          language: languageCode,
          phrasesReviewed: revisionResults.length,
          score: masteredResults.length
        }
      }));

      const refreshKey = `sentence-revision-refresh-${languageCode}`;
      localStorage.setItem(refreshKey, Date.now().toString());

  
      setSaveCompleted(true);

    } catch (error) {
alert(`Erreur lors de la sauvegarde: ${error instanceof Error ? error.message : String(error)}`);
    } finally {
      setIsSaving(false);
    }

  
  }, [
    revisionResults,
    languageCode,
    saveProgressOnSummary,
    updateRevisionProgress,
    userProgress,
    playSummaryAudio
  ]);

  // ✅ FONCTION POUR RELANCER UNE RÉVISION
  const handleStartNewRevision = useCallback(() => {
    const all = getAllLearnedSentences();
    const allSentences = filterCategories && filterCategories.length > 0
      ? all.filter(s => filterCategories.includes(s.category || ''))
      : all;
    const shuffled = [...allSentences].sort(() => Math.random() - 0.5);
    const sentencesToRevise = shuffled.slice(0, Math.min(5, shuffled.length)); // ✅ Maximum 5 phrases
    
  
    
    setRevisionSentences(sentencesToRevise);
    setCurrentSentenceIndex(0);
    setScore(0);
    setTotalPhrases(sentencesToRevise.length);
    setRevisionResults([]);
    setSaveCompleted(false);
    setMode('test');
    
    const modes = languageCode === 'ka' ? ['drag-drop', 'translation'] : ['drag-drop', 'text-input', 'translation'];
    const randomMode = modes[Math.floor(Math.random() * modes.length)] as 'drag-drop' | 'text-input' | 'translation';
    setRevisionMode(randomMode);

    if (randomMode === 'drag-drop') {
      prepareRevisionSentence(sentencesToRevise[0]);
    } else if (randomMode === 'translation') {
      prepareTranslationMode(sentencesToRevise[0]);
    } else {
      setTextInput('');
      setShowTextCorrection(false);
      setIsCorrect(false);
    }
  }, [getAllLearnedSentences, prepareRevisionSentence, prepareTranslationMode, filterCategories]);

  // Helper : passer à la phrase suivante depuis un panneau de correction
  const goToNext = useCallback((clearFn: () => void) => {
    clearFn();
    setAttempts(0);
    if (currentSentenceIndex < revisionSentences.length - 1) {
      const nextIndex = currentSentenceIndex + 1;
      setCurrentSentenceIndex(nextIndex);
      const modes = languageCode === 'ka'
        ? (['drag-drop', 'translation'] as const)
        : (['drag-drop', 'text-input', 'translation'] as const);
      const nextMode = modes[Math.floor(Math.random() * modes.length)];
      setRevisionMode(nextMode);
      if (nextMode === 'drag-drop') {
        prepareRevisionSentence(revisionSentences[nextIndex]);
      } else if (nextMode === 'translation') {
        prepareTranslationMode(revisionSentences[nextIndex]);
      } else {
        setTextInput('');
        setShowTextCorrection(false);
        setIsCorrect(false);
      }
    } else {
      setMode('summary');
    }
  }, [currentSentenceIndex, revisionSentences, prepareRevisionSentence, prepareTranslationMode, languageCode]);

  // Phrase courante
  const currentSentence = revisionSentences[currentSentenceIndex];

  return (
    <DndProvider backend={HTML5Backend}>
      <div className="lm-screen" data-theme={theme}>

        {/* Bouton retour */}
        <FloatingBackButton
          onBack={() => {
            if (mode === 'summary') {
              onBackToCategories();
            } else if (disableConfirmationModal) {
              onBackToCategories();
            } else {
              handleNavigationWithConfirmation(onBackToCategories);
            }
          }}
          position="top-left"
          variant="minimal"
        />

        {/* Barre de progression — style lm-topbar */}
        {mode === 'test' && (
          <div className="lm-topbar">
            <div className="lm-topbar__track">
              <motion.div
                className="lm-topbar__fill"
                animate={{ width: `${((currentSentenceIndex + 1) / totalPhrases) * 100}%` }}
                transition={{ duration: 0.4 }}
              />
            </div>
            <span className="lm-topbar__count">{currentSentenceIndex + 1}/{totalPhrases}</span>
          </div>
        )}

        {/* Mode test */}
        {mode === 'test' && currentSentence && (
          <div className="lm-test">

            {/* Carte arch — question */}
            <div className="lm-arch-card">
              <div className="lm-arch-card__dome">
                <span className="lm-arch-card__deco-top">◇ ◁ ◇ ▷ ◇ ◁ ◇ ▷ ◇</span>
                <p className="lm-arch-card__label">
                  {revisionMode === 'drag-drop' ? t('sentenceConstruction.reconstructSentence') :
                   revisionMode === 'translation' ? t('sentenceConstruction.translateIntoFrenchNoColon') :
                   t('sentenceRevision.translateSentence')}
                </p>
                <h2 className="lm-arch-card__word">
                  {revisionMode === 'translation' && translationState
                    ? translationState.wolofSentence
                    : getSentenceTranslation(currentSentence, i18n.language)}
                </h2>
                {currentSentence.audio && (
                  <div className="lm-audio-wrap">
                    <motion.button
                      className="lm-audio-btn"
                      onClick={() => handlePlayAudio(currentSentence.audio)}
                      whileTap={{ scale: 0.93 }}
                    >
                      <Volume2 size={20} />
                    </motion.button>
                    <span className="lm-audio-label">{t('learnMode.listenButton')}</span>
                  </div>
                )}
                <span className="lm-arch-card__deco-bottom">∨ ∨ ∨ ∨ ∨ ∨ ∨ ∨ ∨</span>
              </div>
            </div>

            {/* MODE DRAG-DROP */}
            {revisionMode === 'drag-drop' && (
              <>
                <div className="modern-construction-zone">
                  <div className={`modern-construction-area ${isCorrect ? 'success' : ''}`}>
                    {constructedWords.length === 0 ? (
                      <div className="construction-placeholder">{t('sentenceRevision.dragWordsHere')}</div>
                    ) : (
                      constructedWords.map((word, index) => (
                        <DraggableWord
                          key={word.id}
                          word={word}
                          index={index}
                          moveWord={moveWord}
                          isCorrect={isCorrect}
                          onWordClick={removeWordByClick}
                        />
                      ))
                    )}
                  </div>
                  <div className="modern-available-words">
                    {availableWords.map((word) => (
                      <div
                        key={word.id}
                        className="modern-word-item available-word"
                        onClick={() => handleWordDrop(word)}
                      >
                        {word.text}
                      </div>
                    ))}
                  </div>
                </div>

                {showCorrection && !isCorrect && (
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="lm-correction-panel"
                  >
                    <span className="lm-correction-panel__label">{t('grammar.goodAnswer')}</span>
                    <p className="lm-correction-panel__answer">{currentSentence.original}</p>
                    <div className="lm-correction-panel__actions">
                      {currentSentence.audio && (
                        <button onClick={() => handlePlayAudio(currentSentence.audio)} className="lm-correction-panel__listen-btn">
                          <Volume2 size={15} /> {t('learnMode.listenButton')}
                        </button>
                      )}
                      <button onClick={() => goToNext(() => setShowCorrection(false))} className="lm-next-btn" style={{ flex: 2 }}>
                        {t('sentenceConstruction.nextArrow')}
                      </button>
                    </div>
                  </motion.div>
                )}

                <div className="lm-sentence-actions">
                  <button onClick={handleReset} className="modern-reset-button">
                    <RotateCw size={16} /> {t('sentenceRevision.restart')}
                  </button>
                  <button
                    onClick={() => handleRevisionAnswer(constructedWords.map(w => w.text).join(' '))}
                    className="lm-next-btn"
                    disabled={constructedWords.length === 0}
                  >
                    <CheckCircle size={16} /> {t('sentenceRevision.validate')}
                  </button>
                </div>
              </>
            )}

            {/* MODE TEXT-INPUT */}
            {revisionMode === 'text-input' && (
              <>
                <div className="modern-text-input-zone">
                  <input
                    type="text"
                    value={textInput}
                    onChange={(e) => setTextInput(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter' && textInput.trim()) handleTextInputAnswer(); }}
                    placeholder={t('sentenceRevision.typeSentence') as string}
                    className="modern-text-input"
                    disabled={isCorrect}
                    autoFocus
                  />
                </div>

                {showTextCorrection && !isCorrect && (
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="lm-correction-panel"
                  >
                    <span className="lm-correction-panel__label">{t('grammar.goodAnswer')}</span>
                    <p className="lm-correction-panel__answer">{currentSentence.original}</p>
                    <div className="lm-correction-panel__actions">
                      {currentSentence.audio && (
                        <button onClick={() => handlePlayAudio(currentSentence.audio)} className="lm-correction-panel__listen-btn">
                          <Volume2 size={15} /> {t('learnMode.listenButton')}
                        </button>
                      )}
                      <button onClick={() => goToNext(() => setShowTextCorrection(false))} className="lm-next-btn" style={{ flex: 2 }}>
                        {t('sentenceConstruction.nextArrow')}
                      </button>
                    </div>
                  </motion.div>
                )}

                <div className="lm-sentence-actions">
                  <button
                    onClick={() => { setTextInput(''); setShowTextCorrection(false); setIsCorrect(false); }}
                    className="modern-reset-button"
                  >
                    <RotateCw size={16} /> {t('sentenceRevision.clear')}
                  </button>
                  <button
                    onClick={handleTextInputAnswer}
                    className="lm-next-btn"
                    disabled={!textInput.trim()}
                  >
                    <CheckCircle size={16} /> {t('sentenceRevision.validate')}
                  </button>
                </div>
              </>
            )}

            {/* MODE TRANSLATION */}
            {revisionMode === 'translation' && translationState && (
              <>
                <div className="modern-construction-zone">
                  <TranslationDropZone
                    constructedWords={translationState.constructedTranslation}
                    onWordDrop={handleFrenchWordDrop}
                    moveWord={moveTranslationWord}
                    isCorrect={isTranslationCorrect}
                    onWordClick={removeFrenchWordByClick}
                  />
                  <div className="modern-available-words">
                    {translationState.availableFrenchWords.map((word) => (
                      <div
                        key={word.id}
                        className="modern-word-item available-french-word"
                        onClick={() => handleFrenchWordDrop(word)}
                      >
                        {word.text}
                      </div>
                    ))}
                  </div>
                </div>

                {showTranslationCorrection && !isTranslationCorrect && (
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="lm-correction-panel"
                  >
                    <span className="lm-correction-panel__label">{t('grammar.goodAnswer')}</span>
                    <p className="lm-correction-panel__answer">{translationState.correctFrenchWords.join(' ')}</p>
                    <div className="lm-correction-panel__actions">
                      {currentSentence.audio && (
                        <button onClick={() => handlePlayAudio(currentSentence.audio)} className="lm-correction-panel__listen-btn">
                          <Volume2 size={15} /> {t('learnMode.listenButton')}
                        </button>
                      )}
                      <button onClick={() => goToNext(() => setShowTranslationCorrection(false))} className="lm-next-btn" style={{ flex: 2 }}>
                        {t('sentenceConstruction.nextArrow')}
                      </button>
                    </div>
                  </motion.div>
                )}

                <div className="lm-sentence-actions">
                  <button onClick={handleReset} className="modern-reset-button">
                    <RotateCw size={16} /> {t('sentenceRevision.restart')}
                  </button>
                  <button
                    onClick={handleTranslationAnswer}
                    className="lm-next-btn"
                    disabled={translationState.constructedTranslation.length === 0}
                  >
                    <CheckCircle size={16} /> {t('sentenceRevision.validate')}
                  </button>
                </div>
              </>
            )}
          </div>
        )}

        {/* Résumé — harmonisé avec SummaryScreen */}
        {mode === 'summary' && (() => {
          const correctCount = revisionResults.filter(r => r.isCorrect).length;
          const totalCount  = revisionResults.length;
          const accuracy    = totalCount > 0 ? Math.round((correctCount / totalCount) * 100) : 0;
          const masteredList = revisionResults.filter(r => r.isCorrect);
          const reviewList   = revisionResults.filter(r => !r.isCorrect);
          return (
            <SummaryScreen
              theme={theme}
              label={t('revision.summaryLabel')}
              congrats={summaryCongrats}
              stats={[
                { value: `${accuracy}%`,           label: t('learnMode.summary.accuracy') },
                { value: `${correctCount}/${totalCount}`, label: t('sentenceConstruction.succeeded') },
                { value: `${totalPhrases}`,         label: t('sentenceConstruction.categorySectionLabel') },
              ]}
              proverb={summaryProverb}
              primaryBtn={{
                label: isSaving ? t('sentenceRevision.saving') : t('sentenceRevision.newRevision'),
                onClick: async () => {
                  if (!saveCompleted && saveProgressOnSummary) await handleRevisionSummaryComplete();
                  handleStartNewRevision();
                },
              }}
              secondaryBtn={{
                label: t('sentenceRevision.backToSentences'),
                onClick: async () => {
                  if (!saveCompleted && saveProgressOnSummary) await handleRevisionSummaryComplete();
                  onBackToCategories();
                },
              }}
              onBack={onBackToCategories}
            >
              {masteredList.length > 0 && (
                <div className="summ-word-section">
                  <p className="summ-word-section__title">{t('sentenceRevision.succeededCount', { count: masteredList.length })}</p>
                  {masteredList.map((r, i) => (
                    <div key={i} className="summ-word-row">
                      <span className="summ-word-row__word">{r.sentence.original}</span>
                      <span className="summ-word-row__badge summ-word-row__badge--ok">✓</span>
                    </div>
                  ))}
                </div>
              )}
              {reviewList.length > 0 && (
                <div className="summ-word-section">
                  <p className="summ-word-section__title">{t('learnMode.summary.toReviewCount', { count: reviewList.length })}</p>
                  {reviewList.map((r, i) => (
                    <div key={i} className="summ-word-row">
                      <span className="summ-word-row__word">{r.sentence.original}</span>
                      <span className="summ-word-row__badge summ-word-row__badge--miss">↺</span>
                    </div>
                  ))}
                </div>
              )}
            </SummaryScreen>
          );
        })()}

        {/* Modal de confirmation de sortie */}
        {!disableConfirmationModal && (
          <ConfirmationModal
            isOpen={showExitModal}
            onConfirm={handleExitConfirm}
            onCancel={handleExitCancel}
            title={t('revision.exitConfirm.title') as string}
            message={`${t('sentenceRevision.exitModal.inProgress')}${revisionResults.length > 0 ? t('sentenceRevision.exitModal.notSavedCount', { count: revisionResults.length }) : t('sentenceRevision.exitModal.notSavedGeneric')}${t('sentenceConstruction.exitModal.confirmQuestion')}`}
            confirmText={t('sentenceConstruction.exitModal.confirm') as string}
            cancelText={t('revision.exitConfirm.cancel') as string}
            confirmButtonStyle="danger"
          />
        )}
      </div>
    </DndProvider>
  );
};

export default SentenceRevisionGame;
